import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { adminAPI } from '@utils/api'
import { useLogo } from '@hooks/useLogo'
import { useAdminAuth } from '@hooks/useAdminAuth'
export { useAdminAuth } from '@hooks/useAdminAuth'
import {
  LayoutDashboard, Users, BookOpen, CalendarDays, MessageSquare,
  Settings, LogOut, Menu, X, Bell, Inbox, Loader2, Globe,
  ChevronLeft, ChevronRight, User, PanelLeftClose, PanelLeft
} from 'lucide-react'

const navItems = [
  { label: 'Dashboard',    path: '/admin',              icon: LayoutDashboard },
  { label: 'Leads',        path: '/admin/leads',         icon: Users },
  { label: 'Contacts',     path: '/admin/contacts',      icon: Inbox },
  { label: 'Courses',      path: '/admin/courses',       icon: BookOpen },
  { label: 'Batches',      path: '/admin/batches',       icon: CalendarDays },
  { label: 'Testimonials', path: '/admin/testimonials',  icon: MessageSquare },
  { label: 'Settings',     path: '/admin/settings',      icon: Settings },
]

const AdminLayout = ({ children }) => {
  const [mobileOpen,   setMobileOpen]   = useState(false)
  const [compact,      setCompact]      = useState(false)
  const [userMenu,     setUserMenu]     = useState(false)
  const [authChecking, setAuthChecking] = useState(true)
  const { logo, adminLogo } = useLogo()
  const FALLBACK = '/deoriginlabs-logo.png'
  const logoSrc  = (adminLogo && adminLogo !== FALLBACK) ? adminLogo : logo
  const userMenuRef                     = useRef(null)
  const location   = useLocation()
  const navigate   = useNavigate()
  const { isAuthenticated, user } = useAdminAuth()

  useEffect(() => {
    adminAPI.me()
      .then(res => {
        if (res?.data?.admin) {
          localStorage.setItem('adminUser', JSON.stringify(res.data.admin))
        }
        setAuthChecking(false)
      })
      .catch(() => {
        setAuthChecking(false)
        if (!localStorage.getItem('adminUser')) {
          navigate('/admin/login')
        }
      })
  }, [])

  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const logout = async () => {
    try { await adminAPI.logout() } catch {}
    localStorage.removeItem('adminUser')
    navigate('/admin/login')
  }

  const currentPage = navItems.find(
    n => n.path === location.pathname || (n.path !== '/admin' && location.pathname.startsWith(n.path))
  )

  if (authChecking) return (
    <div className="h-screen flex items-center justify-center bg-gray-950">
      <Loader2 className="w-7 h-7 animate-spin text-primary-500" />
    </div>
  )

  if (!isAuthenticated) {
    navigate('/admin/login')
    return null
  }

  const sidebarW = compact ? 'w-16' : 'w-60'

  return (
    <div className="h-screen overflow-hidden bg-gray-50 dark:bg-gray-950 flex">
      <style>{`
        .sidebar-scroll::-webkit-scrollbar { width: 3px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 999px; }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.18); }
        .sidebar-scroll { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.08) transparent; }
        .main-scroll::-webkit-scrollbar { width: 4px; }
        .main-scroll::-webkit-scrollbar-track { background: transparent; }
        .main-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 999px; }
        .main-scroll::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.22); }
        .main-scroll { scrollbar-width: thin; scrollbar-color: rgba(0,0,0,0.12) transparent; }
      `}</style>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-20 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* ── Sidebar — fixed height, own scroll ── */}
      <aside className={`
        fixed lg:relative inset-y-0 left-0 z-30
        flex flex-col h-screen shrink-0
        bg-[#0f1117] border-r border-white/[0.06]
        transition-all duration-300 ease-in-out
        ${sidebarW}
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>

        {/* Logo — fixed, never scrolls */}
        <div className={`flex items-center shrink-0 border-b border-white/[0.06] h-14 ${compact ? 'justify-center px-3' : 'justify-between px-4'}`}>
          {compact ? (
            <img src={logoSrc} alt="Deoriginlabs" className="w-20 h-20 object-contain" />
          ) : (
            <>
              <div className="flex items-center gap-2.5 min-w-0">
                <img
                  src={logoSrc}
                  alt="Deoriginlabs"
                  className="h-32 w-auto max-w-[320px] object-contain"
                />
              </div>
              <button onClick={() => setMobileOpen(false)} className="lg:hidden text-gray-500 hover:text-white p-1">
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* Nav — scrollable with slim bar */}
        <nav className="flex-1 py-2.5 px-2 space-y-0.5 overflow-y-auto overflow-x-hidden sidebar-scroll">
          {navItems.map((item) => {
            const Icon = item.icon
            const active = location.pathname === item.path ||
              (item.path !== '/admin' && location.pathname.startsWith(item.path))
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                title={compact ? item.label : undefined}
                className={`
                  flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-150
                  ${compact ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'}
                  ${active
                    ? 'bg-primary-600/20 text-primary-400 border border-primary-500/25'
                    : 'text-gray-500 hover:bg-white/[0.05] hover:text-gray-200 border border-transparent'
                  }
                `}
              >
                <Icon className={`shrink-0 ${active ? 'w-[18px] h-[18px]' : 'w-4 h-4'}`} />
                {!compact && <span className="truncate">{item.label}</span>}
                {!compact && active && <ChevronRight className="w-3 h-3 ml-auto opacity-50" />}
              </Link>
            )
          })}
        </nav>

        {/* Compact toggle — fixed at bottom, never scrolls */}
        <div className="hidden lg:flex shrink-0 border-t border-white/[0.06] p-2">
          <button
            onClick={() => setCompact(c => !c)}
            title={compact ? 'Expand sidebar' : 'Compact sidebar'}
            className={`w-full flex items-center gap-2 px-2 py-2 rounded-xl text-gray-600 hover:text-gray-200 hover:bg-white/[0.05] transition-all text-xs font-medium ${compact ? 'justify-center' : ''}`}
          >
            {compact
              ? <PanelLeft className="w-4 h-4" />
              : <><PanelLeftClose className="w-4 h-4 shrink-0" /><span>Compact</span></>
            }
          </button>
        </div>
      </aside>

      {/* ── Main — independent scroll column ── */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">

        {/* Header — fixed at top of main column */}
        <header className="shrink-0 h-14 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center px-4 gap-3 z-10">

          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 -ml-1"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-1.5 text-sm min-w-0">
            <span className="text-gray-400 hidden sm:block text-xs">Admin</span>
            <ChevronRight className="w-3 h-3 text-gray-300 hidden sm:block" />
            <span className="font-semibold text-gray-900 dark:text-white text-sm truncate">
              {currentPage?.label || 'Panel'}
            </span>
          </div>

          <div className="flex items-center gap-1 ml-auto">
            <a href="/" target="_blank" rel="noopener noreferrer" title="Visit Website"
              className="p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <Globe className="w-[17px] h-[17px]" />
            </a>

            <button className="relative p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <Bell className="w-[17px] h-[17px]" />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-500 rounded-full ring-2 ring-white dark:ring-gray-900" />
            </button>

            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenu(v => !v)}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ml-1"
              >
                <div className="w-7 h-7 rounded-full overflow-hidden bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {user?.avatar
                    ? <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                    : user?.name?.[0]?.toUpperCase() || 'A'
                  }
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-xs font-semibold text-gray-900 dark:text-white leading-tight truncate max-w-[90px]">{user?.name || 'Admin'}</p>
                  <p className="text-[10px] text-gray-400 truncate max-w-[90px] capitalize">{user?.role || 'admin'}</p>
                </div>
                <ChevronRight className={`w-3 h-3 text-gray-400 transition-transform hidden sm:block ${userMenu ? 'rotate-90' : ''}`} />
              </button>

              {userMenu && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{user?.name || 'Admin'}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
                  </div>
                  <div className="p-1.5">
                    <Link to="/admin/profile" onClick={() => setUserMenu(false)}
                      className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                      <User className="w-4 h-4 text-gray-400" />
                      My Profile
                    </Link>
                    <Link to="/admin/settings" onClick={() => setUserMenu(false)}
                      className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                      <Settings className="w-4 h-4 text-gray-400" />
                      Site Settings
                    </Link>
                    <button onClick={logout}
                      className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content — independent scroll with slim bar */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden main-scroll p-4 sm:p-5 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
